import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ApiService} from "../../Shared/api.service";
import {ToggleDarkModeService} from "../../Shared/toggle-dark-mode.service";
import {env} from "../../environment";
import * as jspdf from 'jspdf';
import html2canvas from 'html2canvas';
import * as am5 from "@amcharts/amcharts5";

declare const chi: any;

@Component({
    selector: 'app-analytics-dashboard',
    templateUrl: './defender-dashboard.component.html',
    styleUrls: ['./defender-dashboard.component.css'],
    standalone: false
})

export class DefenderDashboardComponent implements OnInit {
    map_data: any;
    top_five_ips: any;
    top_five_industries: any;
    heatmap_aggregation: any = null;
    timeline_buckets: any[] = [];
    total_threats = 0;
    total_records = 0;
    category_data: any;
    minimizeMap = false;
    minimizeChart = false;
    minimizeBar = false;
    dropdownLabel = 'Last 24 hours';
    timeline = 'last 24 hours';
    accordions = [
        { 
            title: 'See how Black Lotus Labs protects your business from internet‑based threat actors before they can breach your network perimeter and disrupt operation', 
            text: `Lumen Defender℠ powered by Black Lotus Labs® helps fortify Lumen® Internet On‑Demand connections by intercepting threats at the network edge and delivering detailed threat insights on your network.
            <br><br><ul>
                <li>View threats aimed at your network in real-time, block them based on severity and maintain control with allow, deny and monitor lists.</li>
                <li>Access individual threat details to gain additional insight into malicious hosts such as category, origin, protocol and destination IP.</li>
                <li>Use intuitive dashboards and in-depth reports to quickly identify patterns, trends and anomalies to help you understand the nature of the threats you are facing and devise effective countermeasures.</li>
            </ul>`,
            expanded: true,
        },
    ];

    change_time_period(time_range: string, label: string) {
        this.timeline = time_range;
        this.dropdownLabel = label;
        this.get_defender_data();
    }

    // default_payload_map = [
    //     {'source_location': {'country_name': 'United States', 'city_name': 'Boydton', 'latitude': '36.66', 'longitude': '-78.38'}, 
    //     'threat_list': ['Attack', 'Scan']}, 

    //     {'source_location': {'country_name': 'United States', 'city_name': 'Phoenix', 'latitude': '33.45', 'longitude': '-112.07'}, 
    //     'threat_list': ['Attack', 'Scan']}, 
    // ]
    // map_data = this.default_payload_map;

    default_payload_industries = [
        {"name": "11", "friendly": "Agriculture, Forestry, Fishing and Hunting", "count": 10},
        {"name": "55", "friendly": "Management of Companies and Enterprises", "count": 9},
        {"name": "52", "friendly": "Finance and Insurance", "count": 8},
        {"name": "51", "friendly": "Information", "count": 7},
        {"name": "44", "friendly": "Retail Trade", "count": 6},
        {"name": "31", "friendly": "Manufacturing", "count": 5},
    ]

    naics_sector_names: Record<string, string> = {
        '11': 'Agriculture, Forestry, Fishing and Hunting',
        '21': 'Mining, Quarrying, and Oil and Gas Extraction',
        '22': 'Utilities',
        '23': 'Construction',
        '31': 'Manufacturing', '32': 'Manufacturing', '33': 'Manufacturing',
        '42': 'Wholesale Trade',
        '44': 'Retail Trade', '45': 'Retail Trade',
        '48': 'Transportation and Warehousing', '49': 'Transportation and Warehousing',
        '51': 'Information',
        '52': 'Finance and Insurance',
        '53': 'Real Estate and Rental and Leasing',
        '54': 'Professional, Scientific, and Technical Services',
        '55': 'Management of Companies and Enterprises',
        '56': 'Administrative and Support and Waste Management and Remediation Services',
        '61': 'Educational Services',
        '62': 'Health Care and Social Assistance',
        '71': 'Arts, Entertainment, and Recreation',
        '72': 'Accommodation and Food Services',
        '81': 'Other Services',
        '92': 'Public Administration'
    }
    
    show_threat_map = true;

    isDarkTheme = false;
    @ViewChild('contentToPrint', {static: false}) contentToPrint!: ElementRef;

    constructor(private api: ApiService, private helper: ToggleDarkModeService) {
    }

    ngOnInit() {
        this.helper.selected_mode$.subscribe(mode => {
            this.isDarkTheme = mode;
            //changes to background color to adapt for dark mode
            try {
                this.contentToPrint.nativeElement.style.backgroundColor = this.isDarkTheme ? "#000000" : "#ffffff";
            } catch (error) {
                // Undefined element, ignore, DOM will catch up
            }

        })

        this.get_defender_data();
    }

    ngAfterViewInit(): void {
        const dropdown = document.getElementById('dropdown-1');
        if (dropdown && typeof chi !== 'undefined' && chi.dropdown) {
            chi.dropdown(dropdown);
        }
    }

    minimizedMap(): void {
        this.minimizeMap = !this.minimizeMap;
    }
    minimizedChart(): void {
        this.minimizeChart = !this.minimizeChart;
    }
    minimizedBar(): void {
        this.minimizeBar = !this.minimizeBar;
    }

    get_defender_data() {
        let filenames : Record<string, string> = {
            'last 24 hours': '/24-hours.json',
            'last 7 days': '/7-days.json',
            'last 30 days': '/30-days.json',
        }
        let path = '/defender' + filenames[this.timeline];
        this.api.get(path).subscribe(
            (res: any) => {
                this.total_threats = res[0].total_threats;
                this.total_records = res[0].total_records;
                this.category_data = res[1]?.summary ?? [];
                this.top_five_ips = [...this.category_data].sort((a: any, b: any) => b.count - a.count).slice(0, Math.min(this.category_data.length, 5));
                // res[2] = map buckets, res[3] = naics aggregation, res[4] = time-series
                this.top_five_industries = this.get_top_five_industries(res[3]?.industry_data ?? null);
                this.heatmap_aggregation = res[3]?.industry_data ?? null;
                this.map_data = res[2]?.map_data ?? [];
                this.timeline_buckets = Array.isArray(res[4]?.linechart_data) ? res[4].linechart_data : [];
            },
            err  => {
                console.error('Defender data fetch error:', err);
                this.top_five_ips = [];
                this.top_five_industries = [...this.default_payload_industries].sort((a: any, b: any) => b.count - a.count).slice(0, Math.min(this.default_payload_industries.length, 5));
                this.heatmap_aggregation = null;
                this.map_data = [];
                this.timeline_buckets = [];
                this.category_data = null;
            },
        );
    }

    private get_top_five_industries(naics_aggregation: any): any[] {
        if (!naics_aggregation) {
            return [];
        }
        
        if (Array.isArray(naics_aggregation)) {
            return naics_aggregation
                .filter((item: any) => item.naics_sector && item.naics_sector !== 'Unknown')
                .map((item: any) => ({
                    name: item.naics_sector,
                    friendly: this.naics_sector_names[item.naics_sector] || ('NAICS ' + item.naics_sector),
                    count: item.count
                }))
                .sort((a: any, b: any) => b.count - a.count)
                .slice(0, 5);
        }

        return Object.entries(naics_aggregation)
            .filter(([sector]) => !!sector && sector !== 'Unknown')
            .map(([sector, metrics]: [string, any]) => ({
                name: sector,
                friendly: this.naics_sector_names[sector] || ('NAICS ' + sector),
                count: Number(metrics?.total_count ?? metrics?.count ?? 0)
            }))
            .sort((a: any, b: any) => b.count - a.count)
            .slice(0, 5);
    }

    toggleAccordion(index: number) {
        this.accordions[index].expanded = !this.accordions[index].expanded;
    }
    
    saveAsPDF() {
        const content = this.contentToPrint.nativeElement;

        html2canvas(content).then((canvas) => {
            // Convert the canvas to an image
            const imgData = canvas.toDataURL('image/png');

            // Set the image dimensions to fit the PDF
            const imgWidth = 210; // A4 width in mm
            const pageHeight = imgWidth * 1.414; // A4 aspect ratio
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;

            // Create a new PDF document
            const doc = new jspdf.jsPDF('p', 'mm', 'a4');
            let position = 0;
            //changes to background color to adapt for dark mode
            if (this.isDarkTheme) {
                doc.setFillColor("#212529");

            } else {
                doc.setFillColor("#FFFFFF");

            }
            doc.rect(0, 0, 210, 297, 'F');
            // Add the image to the PDF
            doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            // Add more pages if necessary
            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                doc.addPage();
                doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            const confirmSave = window.confirm('Do you want to save this page as pdf?');
            // Save the PDF
            if (confirmSave) {
                doc.save('charts.pdf');
            }
        });
    }


}
